import React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import {
  HeaderBusiness,
  OrButton,
  OrCard,
  OrCheckbox,
  OrIcon,
  OrInput,
  OrLogo,
  OrModal,
  SidebarBusiness,
} from "../../shared_modules/ui/components";
import { MdiEye, MdiEyeOff } from "../../shared_modules/ui/components/atoms/or-icon";
import "./marketing-pre-register.css";
import { useMarketingPreRegister } from "./marketing-pre-register.hook";
import { MarketingTrackingConsent } from "./marketing-tracking-consent";

const MARKETING_SEARCH_PARAMS = new Set(["or_source", "gad_source", "gclid", "gbraid", "wbraid", "fbclid", "msclkid"]);

function noop(): void {}

function ArrowIcon(): React.ReactElement {
  return (
    <svg fill="none" height="20" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="20">
      <line x1="7" x2="17" y1="17" y2="7" />
      <polyline points="7 7 17 7 17 17" />
    </svg>
  );
}

export function isMarketingSearchParam(paramName: string): boolean {
  const normalizedParamName = paramName.trim().toLowerCase();

  return (
    normalizedParamName === "utm" ||
    normalizedParamName.startsWith("utm_") ||
    MARKETING_SEARCH_PARAMS.has(normalizedParamName)
  );
}

export function hasMarketingSearchParams(searchParams: URLSearchParams): boolean {
  for (const key of searchParams.keys()) {
    if (isMarketingSearchParam(key)) {
      return true;
    }
  }

  return false;
}

export { getCompanyNameFromEmail } from "./marketing-pre-register.hook";

export function MarketingPreRegister(): React.ReactElement {
  const {
    employeeCards,
    errors,
    formValues,
    handleEmailChange,
    handlePasswordChange,
    handlePolicyChange,
    handleRecaptchaChange,
    handleSubmit,
    headerMenuItems,
    humanResourcesCards,
    isCaptchaEnabled,
    isSubmitting,
    officeCards,
    setShowPassword,
    sidebarItems,
    successEmail,
    summaryCard,
    supportCards,
  } = useMarketingPreRegister();

  return (
    <div className="marketing-pre-register">
      <div aria-hidden="true" className="marketing-pre-register__preview">
        <SidebarBusiness
          ariaLabel="Demostración de navegación"
          isCollapsed={false}
          items={sidebarItems}
          logo={<OrLogo className="marketing-pre-register__logo" size="sm" />}
        />

        <HeaderBusiness
          companyName="Openreferences"
          menuItems={headerMenuItems}
          notificationCount={0}
          onMobileMenuToggle={noop}
          onNotificationClick={noop}
          onSupportClick={noop}
        />

        <main className="marketing-pre-register__main">
          <div className="business-home">
            <div className="business-home__row business-home__row--main">
              <section className="business-home__section business-home__section--summary">
                <OrCard {...summaryCard} className="business-home__card business-home__card--summary" />
              </section>

              <section className="business-home__section business-home__section--employees">
                <h2 className="business-home__section-title">Empleados</h2>
                <ul className="business-home__list">
                  {employeeCards.map((card) => (
                    <li className="business-home__item" key={card.title}>
                      <OrCard {...card} className="business-home__card" />
                    </li>
                  ))}
                </ul>

                <div className="business-home__banner">
                  <div className="business-home__banner-header">
                    <div className="business-home__banner-content">
                      <h3 className="business-home__banner-title">¿Buscas talento especializado?</h3>
                      <p className="business-home__banner-description">
                        Nuestros expertos encontrarán el perfil ideal para tu empresa.
                      </p>
                    </div>
                    <span className="business-home__banner-arrow">
                      <ArrowIcon />
                    </span>
                  </div>
                  <div className="business-home__banner-actions">
                    <div className="business-home__banner-item">
                      <OrIcon name="check" size={12} />
                      <span>Selección experta</span>
                    </div>
                    <div className="business-home__banner-item">
                      <OrIcon name="check" size={12} />
                      <span>Asesoría personalizada</span>
                    </div>
                    <div className="business-home__banner-discount">DTO. HASTA 50% EN PROCESOS</div>
                  </div>
                </div>
              </section>
            </div>

            <div className="business-home__row business-home__row--secondary">
              <section className="business-home__section">
                <h2 className="business-home__section-title">Oficinas</h2>
                <ul className="business-home__list business-home__list--single">
                  {officeCards.map((card) => (
                    <li className="business-home__item business-home__item--single" key={card.title}>
                      <OrCard {...card} className="business-home__card" />
                    </li>
                  ))}
                </ul>
              </section>

              <section className="business-home__section">
                <h2 className="business-home__section-title">Recursos Humanos</h2>
                <ul className="business-home__list business-home__list--single">
                  {humanResourcesCards.map((card) => (
                    <li className="business-home__item business-home__item--single" key={card.title}>
                      <OrCard {...card} className="business-home__card" />
                    </li>
                  ))}
                </ul>
              </section>
            </div>

            <div className="business-home__row">
              <section className="business-home__section">
                <h2 className="business-home__section-title">Ayuda y soporte</h2>
                <ul className="business-home__list business-home__list--single">
                  {supportCards.map((card) => (
                    <li className="business-home__item business-home__item--single" key={card.title}>
                      <OrCard {...card} className="business-home__card" />
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          </div>
        </main>
      </div>

      <OrModal
        bodyClassName="marketing-pre-register__modal-body"
        className="marketing-pre-register__modal"
        closeOnEscape={false}
        closeOnOverlayClick={false}
        mobileBehavior="bottom-sheet"
        onClose={noop}
        open
        showCloseButton={false}
        size="md"
        title="Empieza tu prueba gratuita"
      >
        {!successEmail ? (
          <div className="marketing-pre-register__modal-copy">
            <h1 className="marketing-pre-register__headline">La confianza se consigue con el poder de la demostración</h1>
            <p className="marketing-pre-register__description">
              Por ese motivo tienes 14 días de prueba gratuita de todas las funcionalidades de nuestra app.
            </p>
          </div>
        ) : null}

        {successEmail ? (
          <div className="marketing-pre-register__success" role="status">
            <h2 className="marketing-pre-register__headline marketing-pre-register__headline--success">
              Ya casi está: activa tu cuenta desde el correo.
            </h2>
            <p className="marketing-pre-register__description">
              Te hemos enviado el enlace a <strong>{successEmail}</strong>. Al confirmarlo, tu prueba gratuita quedará lista.
            </p>
          </div>
        ) : (
          <form className="marketing-pre-register__form" noValidate onSubmit={handleSubmit}>
            <OrInput
              autoComplete="email"
              errorText={errors.email}
              fullWidth
              hasError={Boolean(errors.email)}
              id="marketing-pre-register-email"
              inputType="email"
              label="Email de empresa"
              name="email"
              onChange={handleEmailChange}
              placeholder="tu@empresa.com"
              size="lg"
              value={formValues.email}
            />

            <OrInput
              autoComplete="new-password"
              errorText={errors.password}
              fullWidth
              hasError={Boolean(errors.password)}
              id="marketing-pre-register-password"
              inputType={formValues.showPassword ? "text" : "password"}
              label="Contraseña"
              name="password"
              onChange={handlePasswordChange}
              placeholder="Mínimo 8 caracteres"
              rightSlot={
                <button
                  aria-label={formValues.showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  className="marketing-pre-register__password-toggle"
                  onClick={() => setShowPassword(!formValues.showPassword)}
                  type="button"
                >
                  {formValues.showPassword ? <MdiEyeOff /> : <MdiEye />}
                </button>
              }
              size="lg"
              value={formValues.password}
            />

            <OrCheckbox
              checked={formValues.policy}
              className="marketing-pre-register__policy"
              error={errors.policy}
              id="marketing-pre-register-policy"
              label={
                <>
                  Acepto los{" "}
                  <a
                    className="marketing-pre-register__policy-link"
                    href="https://openreferences.com/aviso-legal/"
                    rel="noreferrer"
                    target="_blank"
                  >
                    terminos y condiciones
                  </a>
                  , y{" "}
                  <a
                    className="marketing-pre-register__policy-link"
                    href="https://openreferences.com/politica-de-privacidad/"
                    rel="noreferrer"
                    target="_blank"
                  >
                    política de privacidad
                  </a>
                </>
              }
              name="policy"
              onChange={handlePolicyChange}
            />

            {errors.submit ? (
              <p className="marketing-pre-register__form-error" role="alert">
                {errors.submit}
              </p>
            ) : null}

            {isCaptchaEnabled ? (
              <div className="marketing-pre-register__recaptcha">
                <ReCAPTCHA sitekey={process.env.RECAPTCHA_SITE_KEY || ""} onChange={handleRecaptchaChange} />
                {errors.captcha ? (
                  <p className="marketing-pre-register__form-error" role="alert">
                    {errors.captcha}
                  </p>
                ) : null}
              </div>
            ) : null}

            <OrButton
              disabled={!formValues.policy || isSubmitting}
              fullWidth
              isLoading={isSubmitting}
              size="lg"
              type="submit"
              variant="primary"
            >
              Activar prueba gratis
            </OrButton>

            <p className="marketing-pre-register__description marketing-pre-register__description--button-note">
              Después del periodo de prueba por 24€/mes podrás registrar empleados ilimitados y sin permanencia.
            </p>
          </form>
        )}
      </OrModal>

      <MarketingTrackingConsent />
    </div>
  );
}
